import { useState } from "react";
import { courseOptionLabel, type CourseOption } from "../../courses/lib/assign.js";
import { schoolYearOptions } from "../lib/schoolYear.js";
import styles from "../../../shared/components/shell.module.css";

/**
 * Creating a class.
 *
 * The school year is the anchor of the automatic deletion, so the form says so —
 * the promise belongs where it takes effect, not only in a contract.
 */
export function NewClassForm({
  pending,
  failed,
  courses,
  onCreate,
  onCancel,
}: {
  pending: boolean;
  failed: boolean;
  /** Lehrgänge, mit denen eine Klasse beginnen kann. Leer, solange geladen wird. */
  courses: readonly CourseOption[];
  onCreate: (input: { name: string; schoolYear: string; curriculumId: string }) => void;
  onCancel: () => void;
}) {
  const years = schoolYearOptions();
  const [name, setName] = useState("");
  const [year, setYear] = useState(years[0]!);
  const [course, setCourse] = useState<string | null>(null);
  const chosen = course ?? courses[0]?.curriculumId ?? null;

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        if (chosen) onCreate({ name, schoolYear: year, curriculumId: chosen });
      }}
    >
      <h2 className={styles.title} style={{ fontSize: "1.15rem" }}>
        Neue Klasse
      </h2>

      <div className={styles.spaced}>
        <label htmlFor="class-name">Bezeichnung</label>
        <input
          id="class-name"
          className={styles.field}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="3a"
          required
          maxLength={30}
        />
      </div>

      <div className={styles.spaced}>
        <label htmlFor="school-year">Schuljahr</label>
        <select
          id="school-year"
          className={styles.field}
          value={year}
          onChange={(e) => setYear(e.target.value)}
        >
          {years.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
        <p className={styles.subtitle} style={{ marginTop: 6, marginBottom: 0 }}>
          Am 31. August nach Ende des Schuljahres werden Codes und Ergebnisse dieser
          Klasse automatisch gelöscht. Die Klasse selbst bleibt bestehen.
        </p>
      </div>

      {/* Gibt es genau einen möglichen Lehrgang, ist das keine Auswahl, sondern
          eine Zeile Text: eine Frage mit einer einzigen Antwort ist keine. Ab
          zwei steht die Liste da, und zwar mit ALLEN veröffentlichten - auch
          denen mit Vorgänger. Grundkurs 1 kann in Klasse 3 und Grundkurs 2 in
          Klasse 4 stattfinden; die Klasse 4 hat den ersten nie als diese Klasse
          geführt. Der Vorgänger steht in der Beschriftung, damit die Aussage
          nicht verloren geht - erzwungen wird er nur in der Auswahl des Kindes. */}
      <div className={styles.spaced}>
        {courses.length === 1 ? (
          <p className={styles.subtitle} style={{ margin: 0 }}>
            Die Klasse beginnt mit <strong>{courses[0]!.title}</strong>
            {courses[0]!.subtitle ? ` (${courses[0]!.subtitle})` : ""}. Weitere Lehrgänge
            können Sie später hinzufügen.
          </p>
        ) : (
          <>
            <label htmlFor="class-course">Lehrgang</label>
            <select
              id="class-course"
              className={styles.field}
              value={chosen ?? ""}
              onChange={(e) => setCourse(e.target.value)}
            >
              {courses.map((option) => (
                <option key={option.curriculumId} value={option.curriculumId}>
                  {courseOptionLabel(option, courses)}
                </option>
              ))}
            </select>
            <p className={styles.subtitle} style={{ marginTop: 6, marginBottom: 0 }}>
              Weitere Lehrgänge können Sie später in den Einstellungen der Klasse
              hinzufügen.
            </p>
          </>
        )}
      </div>

      {failed && (
        <div className={`${styles.notice} ${styles.noticeError} ${styles.spaced}`}>
          Die Klasse konnte nicht angelegt werden.
        </div>
      )}

      <div className={`${styles.row} ${styles.spaced}`}>
        <button
          type="submit"
          className={styles.button}
          disabled={pending || chosen === null || name.trim() === ""}
        >
          {pending ? "Wird angelegt …" : "Klasse anlegen"}
        </button>
        <button type="button" className={styles.buttonQuiet} onClick={onCancel}>
          Abbrechen
        </button>
      </div>
    </form>
  );
}
